import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import s from './ProjectItem.module.css';
import testIcon from '../../assets/icons/testIconProject.png';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/ArrowLeftIcon.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/ArrowRightIcon.svg';
import { projectsApi } from '../../api/projects';
import { Project, ProjectCard } from '../../types/api.types';

export const ProjectsItem: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentSubProjectIndex, setCurrentSubProjectIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
    const [isAnimating, setIsAnimating] = useState(false);
    const [animationKey, setAnimationKey] = useState(0);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);
    const [isTrackAnimating, setIsTrackAnimating] = useState(false);

    const [mobileImageIndex, setMobileImageIndex] = useState(0);
    const [mobileSubProjectIndex, setMobileSubProjectIndex] = useState(0);

    useEffect(() => {
        if (id) {
            loadProject(Number(id));
        }
    }, [id]);

    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                const projectItemPage = document.querySelector(`.${s.projectItemPage}`);
                if (projectItemPage) {
                    const rect = projectItemPage.getBoundingClientRect();
                    const headerHeight = document.querySelector('header')?.offsetHeight || 0;
                    window.scrollTo({
                        top: rect.top + window.pageYOffset - headerHeight - 20,
                        behavior: 'smooth'
                    });
                }
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [loading]);

    const loadProject = async (projectId: number) => {
        try {
            setLoading(true);
            const data = await projectsApi.getById(projectId);
            setProject(data);
        } catch (error) {
            console.error('Ошибка загрузки проекта:', error);
        } finally {
            setLoading(false);
        }
    };

    const subProjects = project?.cards || [];
    const currentSubProject = subProjects[currentSubProjectIndex] || null;

    const nextImage = () => {
        if (currentSubProject && !isAnimating) {
            setIsAnimating(true);
            setSlideDirection('right');
            setCurrentImageIndex((prev) => (prev + 1) % (currentSubProject.imageUrls?.length || 1));
            setTimeout(() => setIsAnimating(false), 500);
        }
    };

    const prevImage = () => {
        if (currentSubProject && !isAnimating) {
            setIsAnimating(true);
            setSlideDirection('left');
            setCurrentImageIndex((prev) => (prev - 1 + (currentSubProject.imageUrls?.length || 1)) % (currentSubProject.imageUrls?.length || 1));
            setTimeout(() => setIsAnimating(false), 500);
        }
    };

    const nextMobileImage = (subProjectIndex: number) => {
        const subProject = subProjects[subProjectIndex];
        if (subProject && subProject.imageUrls && subProject.imageUrls.length > 1) {
            setMobileSubProjectIndex(subProjectIndex);
            setMobileImageIndex((prev) => (prev + 1) % subProject.imageUrls.length);
        }
    };

    const prevMobileImage = (subProjectIndex: number) => {
        const subProject = subProjects[subProjectIndex];
        if (subProject && subProject.imageUrls && subProject.imageUrls.length > 1) {
            setMobileSubProjectIndex(subProjectIndex);
            setMobileImageIndex((prev) => (prev - 1 + subProject.imageUrls.length) % subProject.imageUrls.length);
        }
    };

    const nextTrack = () => {
        if (subProjects.length > 2 && !isTrackAnimating) {
            setIsTrackAnimating(true);
            setVisibleStartIndex((prev) =>
                prev + 1 > subProjects.length - 2 ? 0 : prev + 1
            );
            setTimeout(() => setIsTrackAnimating(false), 400);
        }
    };

    const prevTrack = () => {
        if (subProjects.length > 2 && !isTrackAnimating) {
            setIsTrackAnimating(true);
            setVisibleStartIndex((prev) =>
                prev - 1 < 0 ? subProjects.length - 2 : prev - 1
            );
            setTimeout(() => setIsTrackAnimating(false), 400);
        }
    };

    const handleImageNavigation = (subProjectIndex: number, imageIndex: number) => {
        if (!isAnimating) {
            const subProject = subProjects[subProjectIndex];
            if (subProject && subProject.imageUrls) {
                const maxIndex = subProject.imageUrls.length;
                setSlideDirection(imageIndex > currentImageIndex ? 'right' : 'left');
                setCurrentImageIndex((imageIndex + maxIndex) % maxIndex);
            }
        }
    };

    const getMobileImage = (subProject: ProjectCard, index: number) => {
        if (!subProject.imageUrls || subProject.imageUrls.length === 0) {
            return testIcon;
        }
        if (subProjects.indexOf(subProject) === mobileSubProjectIndex) {
            return subProject.imageUrls[mobileImageIndex % subProject.imageUrls.length] || testIcon;
        }
        return subProject.imageUrls[0] || testIcon;
    };

    const hasMultipleImages = (subProject: ProjectCard) => {
        return subProject.imageUrls && subProject.imageUrls.length > 1;
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    if (!project) {
        return (
            <div className={s.notFound}>
                <h2>Проект не найден</h2>
                <button onClick={() => navigate('/')} className={s.backButton}>
                    Вернуться к проектам
                </button>
            </div>
        );
    }

    return (
        <div className={s.projectItemPage}>
            <h1 className={s.projectItemTitle}>{project.title}</h1>

            {subProjects.length > 0 && (
                <>
                    <div className={s.subProjectsSection}>
                        {/* Десктопная версия */}
                        <div className={s.subProjectsDesktop}>
                            <div className={s.subProjectsScrollWrapper}>
                                <div
                                    className={s.subProjectsTrack}
                                    style={{
                                        transform: `translateX(-${visibleStartIndex * (100 / 2)}%)`,
                                        transition: isTrackAnimating ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none',
                                    }}
                                >
                                    {subProjects.map((subProject, index) => (
                                        <div key={index} className={s.subProjectCardWrapper}>
                                            <div className={s.subProjectCard}>
                                                <div className={s.subProjectLeft}>
                                                    <h3 className={s.subProjectTitle}>{subProject.title}</h3>
                                                    <p className={s.subProjectDescription}>{subProject.description}</p>
                                                    <div className={s.divider}></div>
                                                    <div className={s.projectItemDetails}>
                                                        {Object.entries(subProject.tags || {}).map(([key, value]) => (
                                                            <div key={key} className={s.detailItem}>
                                                                <span className={s.detailValue}>{key} : {value}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className={s.subProjectRight}>
                                                    <div className={s.imageCarousel}>
                                                        <button
                                                            className={s.arrowButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const currentIdx = subProjects.indexOf(subProject);
                                                                if (currentIdx === currentSubProjectIndex) {
                                                                    prevImage();
                                                                } else {
                                                                    setCurrentSubProjectIndex(currentIdx);
                                                                    setCurrentImageIndex(0);
                                                                    setTimeout(() => prevImage(), 100);
                                                                }
                                                            }}
                                                        >
                                                            <ArrowLeftIcon className={s.arrowIcon}/>
                                                        </button>
                                                        <div className={s.imageWrapper}>
                                                            <img
                                                                key={subProjects.indexOf(subProject) === currentSubProjectIndex ? currentImageIndex : 0}
                                                                src={
                                                                    subProjects.indexOf(subProject) === currentSubProjectIndex
                                                                        ? (subProject.imageUrls?.[currentImageIndex % (subProject.imageUrls?.length || 1)] || testIcon)
                                                                        : (subProject.imageUrls?.[0] || testIcon)
                                                                }
                                                                alt={subProject.title}
                                                                className={`${s.subProjectImage} ${
                                                                    subProjects.indexOf(subProject) === currentSubProjectIndex && isAnimating
                                                                        ? slideDirection === 'right' ? s.slideImageRight : s.slideImageLeft
                                                                        : ''
                                                                }`}
                                                            />
                                                            {subProject.imageUrls && subProject.imageUrls.length > 1 && subProjects.indexOf(subProject) === currentSubProjectIndex && (
                                                                <div className={s.imageIndicators}>
                                                                    {subProject.imageUrls.map((_, idx) => (
                                                                        <span
                                                                            key={idx}
                                                                            className={`${s.indicatorDot} ${idx === currentImageIndex ? s.indicatorDotActive : ''}`}
                                                                            onClick={() => {
                                                                                if (!isAnimating) {
                                                                                    setSlideDirection(idx > currentImageIndex ? 'right' : 'left');
                                                                                    setCurrentImageIndex(idx);
                                                                                }
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button
                                                            className={s.arrowButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const currentIdx = subProjects.indexOf(subProject);
                                                                if (currentIdx === currentSubProjectIndex) {
                                                                    nextImage();
                                                                } else {
                                                                    setCurrentSubProjectIndex(currentIdx);
                                                                    setCurrentImageIndex(0);
                                                                    setTimeout(() => nextImage(), 100);
                                                                }
                                                            }}
                                                        >
                                                            <ArrowRightIcon className={s.arrowIcon}/>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Мобильная версия с каруселью */}
                        <div className={s.subProjectsMobile}>
                            {subProjects.map((subProject, index) => (
                                <div key={index} className={s.subProjectCardMobile}>
                                    <div className={s.subProjectLeftMobile}>
                                        <h3 className={s.subProjectTitleMobile}>{subProject.title}</h3>
                                        <p className={s.subProjectDescriptionMobile}>{subProject.description}</p>
                                        <div className={s.dividerMobile}></div>
                                        <div className={s.projectItemDetailsMobile}>
                                            {Object.entries(subProject.tags || {}).map(([key, value]) => (
                                                <div key={key} className={s.detailItemMobile}>
                                                    <span className={s.detailValueMobile}>{key} : {value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={s.subProjectRightMobile}>
                                        <div className={s.mobileImageCarousel}>
                                            {hasMultipleImages(subProject) && (
                                                <button
                                                    className={`${s.mobileArrowButton} ${s.mobileArrowLeft}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        prevMobileImage(index);
                                                    }}
                                                >
                                                    <ArrowLeftIcon className={s.mobileArrowIcon} />
                                                </button>
                                            )}
                                            <div className={s.mobileImageWrapper}>
                                                <img
                                                    src={getMobileImage(subProject, index)}
                                                    alt={subProject.title}
                                                    className={s.subProjectImageMobile}
                                                />
                                                {hasMultipleImages(subProject) && (
                                                    <div className={s.mobileImageIndicators}>
                                                        {subProject.imageUrls?.map((_, idx) => (
                                                            <span
                                                                key={idx}
                                                                className={`${s.mobileIndicatorDot} ${
                                                                    idx === mobileImageIndex && subProjects.indexOf(subProject) === mobileSubProjectIndex
                                                                        ? s.mobileIndicatorDotActive
                                                                        : ''
                                                                }`}
                                                                onClick={() => {
                                                                    setMobileSubProjectIndex(index);
                                                                    setMobileImageIndex(idx);
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {hasMultipleImages(subProject) && (
                                                <button
                                                    className={`${s.mobileArrowButton} ${s.mobileArrowRight}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        nextMobileImage(index);
                                                    }}
                                                >
                                                    <ArrowRightIcon className={s.mobileArrowIcon} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {subProjects.length > 2 && (
                            <div className={s.subProjectsNav}>
                                <button className={s.subNavButton} onClick={prevTrack}>
                                    <ArrowLeftIcon className={s.navIcon}/>
                                </button>
                                <button className={s.subNavButton} onClick={nextTrack}>
                                    <ArrowRightIcon className={s.navIcon}/>
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};